import React from 'react';
import cn from 'classnames';
import {List} from 'immutable';
import Dialog from 'components/pure/utils/Dialog';

import 'styles/call-card/CallCardDetailsDialog.styl';
import ConstValues from 'utils/const';
import moment from 'moment';
import getFullName from 'utils/getFullName';

const DateFormatter = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZoneName: 'short',
});

const searchFieldsToSuppress = List([
  'Comments',
  'Highlights',
  'Topic Words',
  'Keywords',
  'Has Comments',
  'I Commented',
  'Has Highlights',
  'I Highlighted',
  'Call Content',
  `Doesn't Have Comments`,
  `Doesn't Have Highlights`,
  'Highlighters',
  'CRM Link',
]);

const firstFields = List([
  'Caller',
  'Contact',
  'Title',
  'Account',
  'Opportunity',
  'Campaign',
  'Call Stage',
  'Engagement Stage',
]);

const aliasedFields = List(['Call Stage', 'Engagement Stage']);

const crmFields = new Set(['Contact', 'Account', 'Opportunity']);

/**
 * <CallCardDetailsDialog /> renders a modal material-ui Dialog to show an immutable.OrderedMap
 * object which contains all the info regarding the details of search fields and their values for a given
 * FileUpload object
 */
export default class CallCardDetailsDialog extends React.Component {
  constructor(props) {
    super(props);
    this.onTouchTapCloseButton = this.onTouchTapCloseButton.bind(this);
  }

  /**
   * Callback triggered when the user clicks the close button to the top-right
   */
  onTouchTapCloseButton() {
    this.props.onTouchTapCloseButton();
  }

  /**
   * Return the `leftLabel` for a given call details `details`
   * @param  {object} details The details of a call
   * @return {string}         The left label of the call details
   */
  leftLabel(details) {
    return details.name;
  }

  /**
   * Return the `rightLabel` for a given call details `details`
   * @param  {object} details The details of a call
   * @return {string}         The right label of the call details
   */
  rightLabel(details) {
    return displayValueForDetails(details);
  }

  renderCallerAsRep(details) {
    const agent = {type: details.type, value: details.value, name: 'Rep'};
    return (
      <tr key={agent.name}>
        <td>{this.leftLabel(agent)}</td>
        <td>{this.rightLabel(agent)}</td>
      </tr>
    );
  }

  renderCrmDetail(activityData, name) {
    if (activityData !== null) {
      const entity = activityData.get('crm' + name);
      if (
        entity !== null &&
        !(name === 'Opportunity' && activityData.crmOpportunity.type === 'DIRECT')
      ) {
        const key = name === 'Opportunity' && entity.type === 'CASE' ? 'Case' : name;
        const value = name === 'Contact' ? getFullName(entity) : entity.name;
        return (
          value && (
            <tr key={key}>
              <td>{key}</td>
              <td>{value}</td>
            </tr>
          )
        );
      }
    }
  }

  /**
   * Render the call details.
   * @param  {object} callDetails Details of a call's search field
   * @param activityData
   * @return {array} An array of table rows with call details.
   */
  renderCallDetails(callDetails, activityData) {
    const firstFieldsSet = new Set(firstFields);
    const remainingFields = callDetails
      .map(it => it.name)
      .toList()
      .filter(x => {
        return !firstFieldsSet.has(x);
      })
      .sort();

    const displayOrder = firstFields.concat(remainingFields);
    return displayOrder
      .filter(name => {
        return !searchFieldsToSuppress.includes(name);
      })
      .filter(name => {
        if (crmFields.has(name)) {
          return activityData && activityData['crm' + name] !== undefined;
        } else if (aliasedFields.includes(name)) {
          return true;
        } else {
          const details = callDetails.get(name);
          return details && details.value && !isEmptyArray(details);
        }
      })
      .map(name => {
        if (crmFields.has(name)) {
          return this.renderCrmDetail(activityData, name);
        } else if (name === 'Call Stage') {
          if (activityData !== null && activityData.stage !== null)
            return (
              <tr key="Call Stage">
                <td>Call Stage</td>
                <td>{activityData.stage}</td>
              </tr>
            );
        } else if (name === 'Engagement Stage') {
          if (
            activityData !== null &&
            activityData.crmOpportunity !== null &&
            activityData.crmOpportunity.type !== 'DIRECT' &&
            activityData.crmOpportunity.clientStageName
          )
            return (
              <tr key="Engagement Stage">
                <td>
                  {activityData.crmOpportunity.type === 'CASE' ? 'Case Stage' : 'Opportunity Stage'}
                </td>
                <td>{activityData.crmOpportunity.clientStageName}</td>
              </tr>
            );
        } else if (name === 'Company' && activityData && activityData.get('crmAccount')) {
          return null;
        } else if (name === 'Date') {
          const date = new Date(callDetails.get(name).value);
          return (
            <tr key={name}>
              <td>Date</td>
              <td>{DateFormatter.format(date)}</td>
            </tr>
          );
        } else {
          const details = callDetails.get(name);
          if (name === 'Caller') {
            return this.renderCallerAsRep(details);
          } else {
            return (
              <tr key={name}>
                <td>{this.leftLabel(details)}</td>
                <td>{this.rightLabel(details)}</td>
              </tr>
            );
          }
        }
        return null;
      });
  }

  render() {
    const {callDetails, activity, newCallCard, useDialog} = this.props;

    const details = (
      <div className={cn('details-panel', {'new-call-card': newCallCard})}>
        <table className="call-details-label">
          <tbody>
            {(() => {
              if (callDetails && callDetails.count() > 0) {
                return this.renderCallDetails(callDetails, activity ? activity.data : null);
              }
            })()}
          </tbody>
        </table>
      </div>
    );

    if (useDialog) {
      return (
        <Dialog
          open
          autoScrollBodyContent={true}
          title="Call Details"
          onRequestClose={this.onTouchTapCloseButton}
          bodyStyle={{overflowX: 'auto'}}>
          <div className="call-card-details">{details}</div>
        </Dialog>
      );
    }

    return <div className="call-card-details">{details}</div>;
  }
}

function isEmptyArray(details) {
  return Array.isArray(details.value) && details.value.length === 0;
}

/**
 * Return a string that's displayable for a given details of a search field
 * @param  {object} details The details for a given search field
 * @return {string}         A string value that's displayable on the UI for this given details
 */
function displayValueForDetails(details) {
  if (details && details.value != null && !isEmptyArray(details)) {
    if (details.type === 'infinite_string') {
      if (details.value && details.value.trim() == ',') {
        return '';
      } else {
        return `${details.value}`;
      }
    } else if (details.type === 'boolean') {
      if (details.value) {
        return 'Requested';
      } else {
        return 'Not Requested';
      }
    } else if (details.type === 'date' || details.type === 'datetime') {
      const date = moment(details.value);
      return date.format('MM/DD/YYYY');
    } else if (details.type === 'time') {
      const time = moment(details.value);
      return displayValueForMomentTime(time);
    } else if (details.type === 'numeric') {
      let value = details.value;
      if (
        ConstValues.numericSearchFieldNamesNeedSecondsToMinutesConversion.includes(details.name)
      ) {
        // This is a duration. Convert it to hh:mm:ss
        value = secondsDurationToHHMMSS(value);
        return `${value}`;
      } else {
        if (details.unit_fix_position === 'prefix') {
          return `${details.unit} ${value}`;
        } else if (details.unit_fix_position === 'postfix') {
          return `${value} ${details.unit}`;
        }
      }
    }
    return `${details.value}`;
  }
  return '<None Specified>';
}

/**
 * Return a string formatted from a momentjs object taking into account the browser's timezone
 * @param  {moment} time A momentjs converted object
 * @return {string}      A display string value for the momentjs converted object
 */
function displayValueForMomentTime(time) {
  return time.format('LT');
}

/**
 * Convert a time duration in seconds into a string in 'hh:mm:ss'
 * @param  {integer} _seconds A duration in the number of seconds
 * @return {string}         A string with the format 'hh:mm:ss'
 */
function secondsDurationToHHMMSS(_seconds) {
  const hours = Math.floor(_seconds / 3600);
  let minutes = Math.floor((_seconds - hours * 3600) / 60);
  let seconds = _seconds - hours * 3600 - minutes * 60;

  if (hours > 0 && minutes < 10) {
    minutes = '0' + minutes;
  }
  if (seconds < 10) {
    seconds = '0' + seconds;
  }
  if (hours > 0) {
    return `${hours}:${minutes}:${seconds} hours`;
  } else {
    return `${minutes}:${seconds} minutes`;
  }
}
